'use client';

/**
 * Renders structured legal document text with proper formatting.
 * Detects 제N조 headers, numbered lists, bullet points, and sub-bullets.
 */
export default function DocContent({ content }: { content: string }) {
  const paragraphs = content.split(/\n\n+/);

  return (
    <div className="space-y-4">
      {paragraphs.map((para, i) => {
        const trimmed = para.trim();
        if (!trimmed) return null;

        // Main title (first line matching the doc title pattern)
        if (i === 0 && !trimmed.startsWith('제') && !trimmed.match(/^\d+\./)) {
          return (
            <h2 key={i} className="text-xl font-bold text-gray-900 pb-2 border-b border-gray-200">
              {trimmed}
            </h2>
          );
        }

        // Section header: 제N조 (title) or 부칙
        if (/^제\d+조/.test(trimmed) || trimmed === '부칙') {
          const lines = trimmed.split('\n');
          const heading = lines[0];
          const rest = lines.slice(1).join('\n').trim();
          const match = heading.match(/^(제\d+조)/);
          const sectionId = match ? match[1] : undefined;
          return (
            <div key={i} id={sectionId}>
              <h3 className="text-base font-bold text-gray-900 mt-6 mb-2">{heading}</h3>
              {rest && <DocParagraph text={rest} />}
            </div>
          );
        }

        // Lines with mixed content (numbered + bullets)
        return <DocParagraph key={i} text={trimmed} />;
      })}
    </div>
  );
}

function DocParagraph({ text }: { text: string }) {
  const lines = text.split('\n');

  return (
    <div className="space-y-1.5">
      {lines.map((line, j) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return null;

        // Numbered item: "1. text"
        if (/^\d+\.\s/.test(trimmedLine)) {
          return (
            <p key={j} className="text-sm text-gray-700 leading-relaxed pl-1">
              {trimmedLine}
            </p>
          );
        }

        // Bullet: "* text"
        if (trimmedLine.startsWith('* ')) {
          return (
            <p key={j} className="text-sm text-gray-600 leading-relaxed pl-5">
              <span className="text-gray-400 mr-1.5">•</span>
              {trimmedLine.slice(2)}
            </p>
          );
        }

        // Sub-bullet: "‣ text"
        if (trimmedLine.startsWith('‣')) {
          return (
            <p key={j} className="text-sm text-gray-600 leading-relaxed pl-5">
              {trimmedLine}
            </p>
          );
        }

        // "※" note
        if (trimmedLine.startsWith('※')) {
          return (
            <p key={j} className="text-sm text-gray-500 leading-relaxed pl-1 italic">
              {trimmedLine}
            </p>
          );
        }

        // Regular paragraph
        return (
          <p key={j} className="text-sm text-gray-700 leading-relaxed">
            {trimmedLine}
          </p>
        );
      })}
    </div>
  );
}
